import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  BellIcon,
  PlusIcon,
  FunnelIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { CustomAlert, AlertHistory } from '../types';

const AlertsPage: React.FC = () => {
  const { user } = useAuth();
  const { isFeatureEnabled, setLoading, addNotification } = useApp();
  const [alerts, setAlerts] = useState<CustomAlert[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([]);
  const [loading, setLocalLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<CustomAlert | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const loadAlerts = useCallback(async () => {
    setLocalLoading(true);
    setLoading('alerts', true);
    try {
      const alertsData = await apiService.getAlerts();
      
      // Ensure we're setting an array
      if (Array.isArray(alertsData)) {
        setAlerts(alertsData);
      } else if (alertsData && typeof alertsData === 'object' && 'alerts' in alertsData && Array.isArray((alertsData as any).alerts)) {
        // Handle case where API returns { alerts: [...] }
        setAlerts((alertsData as any).alerts);
      } else {
        console.warn('Unexpected alerts data format:', alertsData);
        setAlerts([]);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
      setAlerts([]); // Ensure we always have an array
      addNotification({
        id: 'alerts-error',
        type: 'error',
        title: 'Error Loading Alerts',
        message: 'Failed to load alerts. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    } finally {
      setLocalLoading(false);
      setLoading('alerts', false);
    }
  }, [setLoading, addNotification]);

  const loadAlertHistory = useCallback(async () => {
    try {
      const historyData = await apiService.getAlertHistory();
      
      // Ensure we're setting an array
      if (Array.isArray(historyData)) {
        setAlertHistory(historyData);
      } else if (historyData && typeof historyData === 'object' && 'history' in historyData && Array.isArray((historyData as any).history)) {
        // Handle case where API returns { history: [...] }
        setAlertHistory((historyData as any).history);
      } else {
        console.warn('Unexpected alert history data format:', historyData);
        setAlertHistory([]);
      }
    } catch (error) {
      console.error('Error loading alert history:', error);
      setAlertHistory([]); // Ensure we always have an array
    }
  }, []);

  useEffect(() => {
    // Don't load data if user is not authenticated
    if (!user) {
      return;
    }
    loadAlerts();
    loadAlertHistory();
  }, [user, loadAlerts, loadAlertHistory]);

  const handleCreateAlert = async (alertData: Partial<CustomAlert>) => {
    try {
      const newAlert = await apiService.createAlert(alertData);
      setAlerts(prev => [...prev, newAlert]);
      setShowCreateModal(false);
      addNotification({
        id: 'alert-created',
        type: 'success',
        title: 'Alert Created',
        message: 'Your alert has been created successfully.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'alert'
      });
    } catch (error) {
      console.error('Error creating alert:', error);
      addNotification({
        id: 'alert-create-error',
        type: 'error',
        title: 'Error Creating Alert',
        message: 'Failed to create alert. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const handleUpdateAlert = async (alertData: Partial<CustomAlert>) => {
    if (!editingAlert) return;
    try {
      const updatedAlert = await apiService.updateAlert(editingAlert.id, alertData);
      setAlerts(prev => prev.map(alert => alert.id === editingAlert.id ? updatedAlert : alert));
      setEditingAlert(null);
      addNotification({
        id: 'alert-updated',
        type: 'success',
        title: 'Alert Updated',
        message: 'Your alert has been updated successfully.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'alert'
      });
    } catch (error) {
      console.error('Error updating alert:', error);
      addNotification({
        id: 'alert-update-error',
        type: 'error',
        title: 'Error Updating Alert',
        message: 'Failed to update alert. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const handleDeleteAlert = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this alert?')) {
      try {
        await apiService.deleteAlert(id);
        setAlerts(prev => prev.filter(alert => alert.id !== id));
        addNotification({
          id: 'alert-deleted',
          type: 'success',
          title: 'Alert Deleted',
          message: 'Your alert has been deleted successfully.',
          timestamp: new Date().toISOString(),
          read: false,
          category: 'alert'
        });
      } catch (error) {
        console.error('Error deleting alert:', error);
        addNotification({
          id: 'alert-delete-error',
          type: 'error',
          title: 'Error Deleting Alert',
          message: 'Failed to delete alert. Please try again.',
          timestamp: new Date().toISOString(),
          read: false,
          category: 'system'
        });
      }
    }
  };

  const handleTestAlert = async (id: string) => {
    try {
      await apiService.testAlert(id);
      addNotification({
        id: 'alert-tested',
        type: 'success',
        title: 'Test Alert Sent',
        message: 'Test alert has been sent successfully.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'alert'
      });
    } catch (error) {
      console.error('Error testing alert:', error);
      addNotification({
        id: 'alert-test-error',
        type: 'error',
        title: 'Error Testing Alert',
        message: 'Failed to send test alert. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'trend_emergence':
        return 'bg-blue-100 text-blue-800';
      case 'momentum_spike':
        return 'bg-green-100 text-green-800';
      case 'viral_content':
        return 'bg-purple-100 text-purple-800';
      case 'engagement_threshold':
        return 'bg-yellow-100 text-yellow-800';
      case 'sentiment_change':
        return 'bg-orange-100 text-orange-800';
      case 'competitor_activity':
        return 'bg-red-100 text-red-800';
      case 'market_opportunity':
        return 'bg-emerald-100 text-emerald-800';
      case 'risk_alert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'retrying':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filterStatus !== 'all' && alert.is_active !== (filterStatus === 'active')) return false;
    if (filterType !== 'all' && alert.alert_type !== filterType) return false;
    return true;
  });

  const filteredHistory = alertHistory.filter(history => {
    if (filterType !== 'all') {
      const alert = alerts.find(a => a.id === history.alert_id);
      return alert?.alert_type === filterType;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
          <p className="text-gray-600">Manage your trend alerts and notifications</p>
        </div>
        {isFeatureEnabled('canCreateCustomAlerts') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Alert
          </button>
        )}
      </div>

      {/* Upgrade CTA for users without access */}
      {!isFeatureEnabled('canCreateCustomAlerts') && (
        <UpgradeCTA 
          title="Smart Alerts"
          description="Create custom alerts to monitor trends and get notified when important changes occur."
          features={[
            "Unlimited smart alerts with real-time monitoring",
            "Custom alert thresholds & conditions",
            "Multi-channel notifications (email, Slack, Teams)",
            "Advanced filtering & smart grouping",
            "Alert history & performance analytics",
            "Webhook integration & API endpoints"
          ]}
          tier="Professional"
        />
      )}

      {/* Content for users with access */}
      {isFeatureEnabled('canCreateCustomAlerts') && (
        <>
          {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Alerts ({filteredAlerts.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Alert History ({filteredHistory.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
          >
            <option value="all">All Types</option>
            <option value="trend_emergence">Trend Emergence</option>
            <option value="momentum_spike">Momentum Spike</option>
            <option value="viral_content">Viral Content</option>
            <option value="engagement_threshold">Engagement Threshold</option>
            <option value="sentiment_change">Sentiment Change</option>
            <option value="competitor_activity">Competitor Activity</option>
            <option value="market_opportunity">Market Opportunity</option>
            <option value="risk_alert">Risk Alert</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading alerts...</p>
        </div>
      ) : activeTab === 'active' ? (
        <div className="space-y-4">
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-12">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts found</h3>
              <p className="text-gray-600">Create your first alert to get started</p>
            </div>
          ) : (
            filteredAlerts.map((alert) => (
              <div key={alert.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{alert.name}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getAlertTypeColor(alert.alert_type)}`}>
                        {alert.alert_type.replace('_', ' ')}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(alert.priority)}`}>
                        {alert.priority}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${alert.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {alert.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {alert.description && (
                      <p className="text-gray-600 text-sm mb-3">{alert.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Created {new Date(alert.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center">
                        <BellIcon className="h-4 w-4 mr-1" />
                        Triggered {alert.trigger_count} times
                      </div>
                      {alert.last_triggered && (
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          Last triggered {new Date(alert.last_triggered).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleTestAlert(alert.id)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Test Alert"
                    >
                      <PlayIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditingAlert(alert)}
                      className="p-2 text-gray-400 hover:text-yellow-600"
                      title="Edit Alert"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete Alert"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No alert history</h3>
              <p className="text-gray-600">Alert history will appear here when alerts are triggered</p>
            </div>
          ) : (
            filteredHistory.map((history) => (
              <div key={history.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{history.alert_name}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(history.notification_status)}`}>
                        {history.notification_status}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(history.impact_assessment.severity)}`}>
                        {history.impact_assessment.severity}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">
                      Triggered at {new Date(history.triggered_at).toLocaleString()}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Trigger Conditions:</p>
                        <ul className="text-gray-700 mt-1">
                          {history.trigger_conditions.map((condition, index) => (
                            <li key={index}>
                              {condition.metric}: {condition.actual_value} (threshold: {condition.threshold})
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-gray-500">Impact Assessment:</p>
                        <p className="text-gray-700 mt-1">{history.impact_assessment.business_impact}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAlert) && (
        <AlertModal
          alert={editingAlert}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAlert(null);
          }}
          onSubmit={editingAlert ? handleUpdateAlert : handleCreateAlert}
        />
      )}
    </div>
  );
};

// Alert Modal Component
interface AlertModalProps {
  alert?: CustomAlert | null;
  onClose: () => void;
  onSubmit: (data: Partial<CustomAlert>) => Promise<void>;
}

const AlertModal: React.FC<AlertModalProps> = ({ alert, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: alert?.name || '',
    description: alert?.description || '',
    alert_type: alert?.alert_type || 'trend_emergence',
    priority: alert?.priority || 'medium',
    is_active: alert?.is_active ?? true,
    criteria: alert?.criteria || { threshold_conditions: [] },
    notification_methods: alert?.notification_methods || ['email'],
    frequency: alert?.frequency || 'immediate'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {alert ? 'Edit Alert' : 'Create New Alert'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alert Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alert Type
              </label>
              <select
                value={formData.alert_type}
                onChange={(e) => setFormData(prev => ({ ...prev, alert_type: e.target.value as any }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="trend_emergence">Trend Emergence</option>
                <option value="momentum_spike">Momentum Spike</option>
                <option value="viral_content">Viral Content</option>
                <option value="engagement_threshold">Engagement Threshold</option>
                <option value="sentiment_change">Sentiment Change</option>
                <option value="competitor_activity">Competitor Activity</option>
                <option value="market_opportunity">Market Opportunity</option>
                <option value="risk_alert">Risk Alert</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value as any }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="immediate">Immediate</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Methods
            </label>
            <div className="space-y-2">
              {['email', 'push', 'sms', 'webhook'].map((method) => (
                <label key={method} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notification_methods.includes(method as any)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          notification_methods: [...prev.notification_methods, method as any]
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          notification_methods: prev.notification_methods.filter(m => m !== method)
                        }));
                      }
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 capitalize">{method}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label className="ml-2 text-sm text-gray-700">Active</label>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {alert ? 'Update Alert' : 'Create Alert'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upgrade CTA Component
interface UpgradeCTAProps {
  title: string;
  description: string;
  features: string[];
  tier: string;
}

const UpgradeCTA: React.FC<UpgradeCTAProps> = ({ title, description, features, tier }) => {
  const handleUpgrade = () => {
    window.location.href = '/pricing';
  };

  return (
    <div className="text-center py-12">
      <div className="max-w-2xl mx-auto">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="h-8 w-8 text-purple-600" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{description}</p>
        
        {/* Features List */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-4">What you'll get:</h4>
          <ul className="text-left space-y-2 max-w-md mx-auto">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <button
          onClick={handleUpgrade}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Upgrade to {tier}
        </button>
      </div>
    </div>
  );
};

export default AlertsPage; 